import { useEffect, useMemo, useState } from 'react';
import { colors } from 'ui/theme/colors';
import { HelpToggletip } from 'ui/tooltip';
import {
  Description,
  GroupLabel,
  HelpToggleTipWrapper,
  Label,
  LeftDiv,
  RadioButton,
  Row,
  StyledTooltip,
  TextWrapper,
  TopText,
  Wrapper,
} from './RadioListStyles';
import { Option, Props, RADIO_LIST_VARIANTS } from './types';

export const RADIO_LIST_PREFIX = 'radio-list-';
export const RADIO_LIST_ITEM_PREFIX = 'radio-list-item-';
export const RADIO_LIST_OPTION_PREFIX = 'radio-list-option-';
export const RADIO_LIST_ICON_PREFIX = 'radio-list-icon-';

/**
 * @deprecated
 * this radiolist implementation has been deprecated. please use ui/RadioGroup
 * */

export const RadioList = ({
  name: groupName,
  label: groupLabel,
  onSelect = () => null,
  options = [],
  value,
  variant,
  isReadOnly = false,
  isRegularWeight = false,
  ...props
}: Props) => {
  // Value is the authoritative state
  const [selectedValue, selectValue] = useState(options[0]?.value);

  useEffect(() => {
    // This keeps internal state in sync, if parent controls selected value
    if (value) selectValue(value);
  }, [value]);

  const onClick = (val: string) => {
    selectValue(val);
    // Trigger parent callback, if present
    onSelect(val);
  };

  // Only allow keyboard selection of non-disabled options
  const selectableOptions: Option[] = useMemo(
    () => options.filter((option: Option) => !option.isDisabled),
    [options],
  );
  const currentIdx = () => selectableOptions.findIndex((option) => option.value === selectedValue);
  const increment = () => {
    const next = currentIdx() + 1;
    if (!(next > selectableOptions.length - 1)) selectValue(selectableOptions[next]?.value);
  };
  const decrement = () => {
    const next = currentIdx() - 1;
    if (next >= 0) selectValue(selectableOptions[next]?.value);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        return decrement();
      case 'ArrowDown':
      case 'ArrowRight':
        return increment();
      default:
        break;
    }
  };

  return (
    <Wrapper
      aria-labelledby={groupName}
      data-test={`${RADIO_LIST_PREFIX}${groupName}`}
      role="radiogroup"
      onKeyDown={onKeyDown}
      {...props}
    >
      {groupLabel && <GroupLabel id={groupName}>{groupLabel}</GroupLabel>}
      {options.map(
        (
          {
            Icon,
            id,
            isDisabled,
            isTooltipEnabled = false,
            isHelperIconEnabled = false,
            helperIconProps = {},
            description,
            label,
            name: Name,
            tooltipContent,
            helperIconContent,
            value: val,
          },
          idx,
        ) => {
          // Variables for styling and functionality
          const isBordered = variant === RADIO_LIST_VARIANTS.BORDERED;
          const isChecked = val === selectedValue;
          const isNonSelectable = isDisabled || isReadOnly;
          // Sub-components
          const hasSupplementalText = Boolean(label) || Boolean(description);
          const NameNode = typeof Name === 'string' ? <span>{Name}</span> : <Name />;
          const DescriptionNode =
            typeof description === 'string' ? (
              <Description>{description}</Description>
            ) : (
              description
            );

          return (
            <StyledTooltip key={id} isEnabled={isTooltipEnabled} tooltipContent={tooltipContent}>
              <Row
                data-test={`${RADIO_LIST_ITEM_PREFIX}${id}`}
                data-disabled={isNonSelectable}
                onClick={() => !isNonSelectable && onClick(val)}
                isBordered={isBordered}
                isChecked={isChecked}
                isDisabled={isNonSelectable}
                isReadOnly={isReadOnly}
              >
                <LeftDiv hasSupplementalText={hasSupplementalText}>
                  {/* Radio button */}
                  <div>
                    <RadioButton
                      hasSupplementalText={hasSupplementalText}
                      id={id}
                      data-test={`${RADIO_LIST_OPTION_PREFIX}${id}`}
                      isChecked={isChecked}
                      isDisabled={isNonSelectable}
                      name={label}
                      onKeyDown={onKeyDown}
                      /**
                       * **** WARNING ****
                       * TODO: The click events here bubble up to `Row` resulting in `onClick` getting called twice per click... need to patch.
                       * @see https://developer.mozilla.org/en-US/docs/Learn/JavaScript/Building_blocks/Events#event_bubbling
                       */
                      onClick={() => !isNonSelectable && onClick(val)}
                      tabIndex={idx === 0 ? 0 : -1}
                      value={val}
                    />
                  </div>

                  {/* Text */}
                  <TextWrapper>
                    <TopText
                      hasSupplementalText={hasSupplementalText}
                      isRegularWeight={isRegularWeight}
                    >
                      {NameNode}
                      {label && <Label>{label}</Label>}
                      {isHelperIconEnabled && (
                        <HelpToggleTipWrapper onClick={(e) => e.stopPropagation()}>
                          <HelpToggletip
                            tooltipContent={helperIconContent}
                            iconProps={helperIconProps}
                          />
                        </HelpToggleTipWrapper>
                      )}
                    </TopText>
                    {DescriptionNode && DescriptionNode}
                  </TextWrapper>
                </LeftDiv>

                {/* Icon */}
                {Icon && isBordered && (
                  <div data-test={`${RADIO_LIST_ICON_PREFIX}${id}`}>
                    <Icon color={colors.grey35} />
                  </div>
                )}
              </Row>
            </StyledTooltip>
          );
        },
      )}
    </Wrapper>
  );
};
